import { nullableOptionSchema, optionSchema } from '@/common/schemas/option.schema'
import { DocumentData } from '@/common/types/document';
import { z } from 'zod'

export const maintenanceSpecificationSchema = z.object({
  id: z.string().optional(),
  directive_klass: z.string().optional(),
  equipment_category_specifications_attributes: z.array(optionSchema).optional(),
  equipment_manufacturer_specifications_attributes: z.array(optionSchema).optional(),
  system_specifications_attributes: z.array(optionSchema).optional(),
});

export type MaintenanceSpecificationValidationSchema = z.infer<typeof maintenanceSpecificationSchema>;

export const maintenanceRequiredDocumentSchema = z.object({
  id: z.string().optional(),
  document: z.object({
    id: z.string(),
    display_name: z.string(),
  }),
  file_attachment_attributes: z.object({
    file_url: z.string().optional(),
    file_name: z.string({ message: 'Anexo obrigatório' }).min(1, { message: 'Campo obrigatório' }),
    blob_id: z.string().optional(),
  }).optional(),
  valid_until: z.string({ message: 'Campo obrigatório' }).min(1, { message: 'Campo obrigatório' }),
  issue_date: z.string({ message: 'Campo obrigatório' }).min(1, { message: 'Campo obrigatório' }),
});

export type MaintenanceRequiredDocumentValidationSchema = z.infer<typeof maintenanceRequiredDocumentSchema>;

export const updateSchema = z.object({
  commercial_manager: optionSchema.optional(),
  commercial_manager_substitute: nullableOptionSchema.optional().nullable(),
  accreditation_information: z.object({
    product_categories: z.array(optionSchema).optional(),
    capture_methods: z.array(
      z.object({
        perma_name: z.string().optional().nullable(),
        label: z.custom<string | JSX.Element>(),
        value: z.string()
      }, { message: 'Campo obrigatório' })
    ).optional(),
    photos: z.array(
      z.object({
        id: z.string(),
        file: z.custom<string | File>(),
      }),
    ),
    accreditation_information_pos_machines: z.object({
      machine_id: z.array(optionSchema).optional(),
    }),
    authorizer_identifier: z.string().optional().nullable(),
    payment_advance_daily_interest: z.string().optional().nullable(),
  }).optional(),
  general_information: z.object({
    cnpj: z.string().optional().nullable(),
    email: z.string().optional().nullable(),
    phone: z.object({
      number: z.string().optional().nullable(),
      kind: z.string().optional().nullable(),
      whatsapp_capability: z.string().optional().nullable(),
    }).optional(),
    fantasy_name: z.string().optional().nullable(),
    official_name: z.string().optional().nullable(),
    primary_cnae: z.string().optional().nullable(),
    simple_national_optant: optionSchema.optional().nullable(),
    state_inscription: z.string().optional().nullable(),
    commercial_kind: optionSchema.nullable(),
    flag: optionSchema.optional(),
    website: z.string().optional().nullable(),
  }).optional(),
  address_attributes: z.object({
    state_id: z.string().optional().nullable(),
    city_id: z.string().optional().nullable(),
    neighborhood: z.string().optional().nullable(),
    number: z.string().optional().nullable(),
    street: z.string().optional().nullable(),
    zipcode: z.string().optional().nullable(),
    latitude: z.string().optional().nullable(),
    longitude: z.string().optional().nullable(),
    complement: z.string().optional().nullable(),
  }).optional(),
  bank_account_information: z.object({
    account: z.string().trim().optional().nullable(),
    agency: z.string().trim().optional().nullable(),
    bank_institution: optionSchema.optional().nullable(),
    payee_name: z.string().trim().optional().nullable().transform((val) => val?.trim()),
    payee_cnpj: z.string().trim().optional().nullable(),
  }).optional(),
  contact_for_master_user: z.object({
    value: z.string().optional().nullable(),
  }).optional().nullable(),
  observations: z.string().optional().nullable(),
  ca_module_enabled: z.boolean().optional().nullable(),
  cm_module_enabled: z.boolean().optional().nullable(),
  maintenance_infrastructures: z.array(optionSchema).optional().nullable(),
  maintenance_specialty: z.object({
    id: z.string().optional().nullable(),
    system_part_id: z.string().optional().nullable(),
    action_id: z.string().optional().nullable(),
  }).optional().nullable(),
  maintenance_technical_professionals: z.array(optionSchema).optional().nullable(),
  maintenance_required_documents_attributes: z.array(maintenanceRequiredDocumentSchema).optional(),
  maintenance_specifications_attributes: z.array(maintenanceSpecificationSchema).optional(),
  tow_service_available: z.boolean().optional().nullable(),
})

export type EstablishmentLeadsFunnelUpdateValidationSchema = z.infer<typeof updateSchema>

export const updateContactSchema = z.object({
  contacts: z.object({
    email: z.string().optional().nullable(),
    full_name: z.string().optional().nullable(),
    role_in_company: z.string().optional().nullable(),
    phone_attributes: z.object({
      number: z.string().optional().nullable(),
      kind: z.string().optional().nullable(),
      whatsapp_capability: z.string().optional().nullable(),
    }).optional(),
    cpf: z.string().optional().nullable(),
    documents: z.array(
      z.object({
        id: z.string().optional().nullable(),
        cnh: z.string().optional().nullable(),
        file: z.custom<Blob>().optional().nullable(),
        issued_on_date: z.string().optional().nullable(),
        expires_at: z.string().optional().nullable(),
        rg: z.string().optional().nullable(),
        type: z.string().optional().nullable(),
        last_review: z.object({
          description: z.string().optional().nullable(),
          state: z.string().optional().nullable(),
        }).optional().nullable(),
        doc_options: z.custom<DocumentData>().optional().nullable(),
        checked: z.boolean().optional().nullable(),
      }).optional(),
    ),
    responsible_for_company_information: z.boolean().optional().nullable(),
  }),
})

export type EstablishmentLeadsFunnelUpdateContactValidationSchema = z.infer<typeof updateContactSchema>

export const updateContractSchema = z.object({
  bill_grouping_mode: z.object({
    value: z.string().optional().nullable(),
  }).optional().nullable(),
  cycle: z.object({
    value: z.string().optional().nullable(),
  }).optional().nullable(),
  cycle_start_day: z.object({
    value: z.string().optional().nullable(),
  }).optional().nullable(),
  closing_to_due_in_days: z.object({
    value: z.string().optional().nullable(),
  }).optional().nullable(),
  cnpj_for_fiscal_document: z.string().optional().nullable(),
  contract_fees: z.array(
    z.object({
      id: z.string(),
      value: z.string(),
      main_fee: z.boolean().optional().nullable(),
      fee_template: z.object({
        id: z.string().optional(),
        display_name: z.string().optional(),
        calculate_as: z.string().optional(),
      }),
    })
  ).optional().nullable(),
  contract_product_categories: z.array(optionSchema).optional().nullable(),
  contract_products: z.array(optionSchema).optional().nullable(),
  credit_granted: z.number().optional().nullable(),
  due_to_suspension_in_days: z.object({
    value: z.string().optional().nullable(),
  }).optional().nullable(),
  duration_in_months: z.number().optional().nullable(),
  file: z.object({
    content_type: z.string().optional().nullable(),
    url: z.string().optional().nullable(),
  }).optional().nullable(),
  fiscal_document_requirement: z.object({
    value: z.string().optional().nullable(),
  }).optional().nullable(),
  in_effect_since: z.string().optional().nullable(),
  in_effect_until: z.string().optional().nullable(),
  payment_method: z.object({
    value: z.string().optional().nullable(),
  }).optional().nullable(),
  payment_modality: z.string().optional().nullable(),
})

export type EstablishmentLeadsFunnelUpdateContractValidationSchema = z.infer<typeof updateContractSchema>

export const sendEstablishmentLeadEquipmentSchema = z.object({
  message: z.string().optional().nullable(),
})

export type SendEstablishmentLeadEquipmentValidationSchema = z.infer<typeof sendEstablishmentLeadEquipmentSchema>
